import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, forwardRef, Injector, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { PATIENT_LIMITS } from 'src/app/shared/app.constants';
import { ConversionLengthService } from '../../services/conversion/conversion-length.service';
import { HeightUnit } from '../header/components/interface/interface';

@Component({
  selector: 'app-height-input',
  standalone: true,
  templateUrl: './height-input.component.html',
  styleUrls: ['./height-input.component.scss'],

  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppHeightInputComponent),
      multi: true,
    },
  ],
  imports: [CommonModule, MatInputModule, FormsModule, MatFormFieldModule],
})
export class AppHeightInputComponent implements ControlValueAccessor, OnChanges {
  HeightUnit = HeightUnit;
  isFocused = false
  isHovered= false
  public ngControl: NgControl | null = null;

  @Input() heightInCm: any = 0;
  @Input() label = 'Height';
  // When true, the imperial mode shows a single inches input instead of
  // separate feet + inches fields (used for neck size).
  @Input() inchesOnly = false;
  @Output() heightChange = new EventEmitter<number>();
  limits = PATIENT_LIMITS['height']
  cm: number | null = null;
  feet: number | null = null;
  inches: number | null = null;
  totalInches: number | null = null;

  private _onChange: (value: number) => void = () => {};
  private _onTouched: () => void = () => {};

  constructor(
    public conversionLengthService: ConversionLengthService,
    private cdr: ChangeDetectorRef,
    private injector: Injector

  ) {}

  ngOnInit(): void {
    try {
      this.ngControl = this.injector.get(NgControl, null);
      if (this.ngControl) {
        this.ngControl.valueAccessor = this;
        this.ngControl.statusChanges?.subscribe(() => {
          this.cdr.markForCheck();
        });
        this.limits = PATIENT_LIMITS[this.ngControl.name]
      }

    } catch (e) {
      console.warn('NgControl not found for height input:', e);
    }
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['heightInCm']) {
      this.updateHeightValues();
    }


  }

  writeValue(value: number): void {
    if (value !== undefined && value !== null) {
      this.heightInCm = value;
      this.updateHeightValues();
    }
  }

  registerOnChange(fn: (value: number) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  updateHeightValues(): void {
  const heightCm = this.heightInCm;
  if (heightCm !== null && heightCm !== undefined) {
    if (this.conversionLengthService.preferredLengthUnit === HeightUnit.IN) {
      // Convert cm to total inches
      const totalInches = heightCm / 2.54;
      this.totalInches = parseFloat(totalInches.toFixed(2));
      this.feet = Math.floor(totalInches / 12);
      this.inches = parseFloat((totalInches % 12).toFixed(2));
      this.cm = null;  // clear cm input when using inches
    } else if (this.conversionLengthService.preferredLengthUnit === HeightUnit.CM) {
      this.cm = heightCm;
      this.feet = null;
      this.inches = null;
      this.totalInches = null;
    }
  } else {
    this.cm = null;
    this.feet = null;
    this.inches = null;
    this.totalInches = null;
  }
}

onInputChange(): void {
  let heightCm: number | null = null;

  if (this.conversionLengthService.preferredLengthUnit === HeightUnit.IN) {
    if (this.inchesOnly) {
      const totalInches = parseFloat(this.totalInches as any);
      if (!isNaN(totalInches)) {
        heightCm = totalInches * 2.54;  // Convert back to cm
      }
    } else {
      const feet = parseFloat(this.feet as any);
      const inches = parseFloat(this.inches as any);

      const isFeetValid = !isNaN(feet);
      const isInchesValid = !isNaN(inches);

      if (isFeetValid || isInchesValid) {
        const totalInches = (isFeetValid ? feet : 0) * 12 + (isInchesValid ? inches : 0);
        heightCm = totalInches * 2.54;  // Convert back to cm
      }
    }
  } else if (this.conversionLengthService.preferredLengthUnit === HeightUnit.CM) {
    const cm = parseFloat(this.cm as any);
    if (!isNaN(cm)) {
      heightCm = cm;
    }
  }

  this.heightInCm = heightCm;

  this._onChange(heightCm);
  this.heightChange.emit(heightCm);
}



  onBlur(): void {
    this._onTouched();
    this.isFocused = false;
  }

  onFocus() {
    this.isFocused = true;
  }

  onMouseEnter(): void {
  this.isHovered = true;
}

onMouseLeave(): void {
  this.isHovered = false;
}



}
